import React, { useImperativeHandle, forwardRef, useState } from 'react';
import { validateInit, validateForm } from '@assets/js/util/formValidation.js';
import { formAtt } from '@assets/js/util/commonUtil.js';
import CompFmtInput from '@components/form/fragment/comp-fmt-input.js';
import CompFmtSelectbox from '@components/form/fragment/comp-fmt-selectbox.js';
import {ApiSave} from '@assets/js/api/commonApi.js';

const CompFormCompetitionForm = forwardRef((props, ref) => {
    const [params, setParams] = useState({});
    const [title, setTitle] = useState('대회폼 등록/수정');
    
    /****************************
     * Define required variables
    *****************************/
    const rootId = 'modalSheet';
    const validateOpt = {
        scopeEle: document.querySelector('#' + rootId),
        targetIds: ['comptnCd', 'injuryUseYn', 'videoUseYn', 'strtDate', 'deadDate', 'useYn'],
        params: params,
    }

    /*************************
     * Define react functions
    **************************/
    // Forward ref to allow parent components to call event functions.
    useImperativeHandle(ref, () => ({
        modalToggle(p_params) {
            handleToggle();
            handleValue(p_params);
            setParams(p_params || {});
        },
    }));

    /**********************
     * Define functions
    ***********************/
    const handleValue = (p_params) => {
        const targetEle = document.querySelector('#' + rootId);

        // Handle mode-specific logic
        if (p_params && p_params.mode === 'M') {
            setTitle('대회폼 수정');
            // Modify mode - disable all code fields
            const comptnCdEle = targetEle?.querySelector('#comptnCd');
            if (comptnCdEle) comptnCdEle.disabled = true;

        } else {
            setTitle('대회폼 등록');
            // Insert mode - enable code fields for selection
            const comptnCdEle = targetEle?.querySelector('#comptnCd');
            if (comptnCdEle) comptnCdEle.disabled = false;
        }
    }
    const handleToggle = () => {
        const targetEle = document.querySelector('#' + rootId);
        if (targetEle) {
            targetEle.style.display = targetEle.style.display === 'none' ? 'block' : 'none';
        }

        // Reset form inputs and validation state
        validateInit(validateOpt);

        Object.keys(formObj).forEach(key => {
            const ele = targetEle?.querySelector(`#${formObj[key].id}`);
            if (ele) { ele.value = ''; }
        });
    }
    const onSave = () => {
        // Validate form inputs before saving
        if (!validateForm(validateOpt)) { return; }
        if (params == null || params.comptnCd === '') { return; }

        // Create mtmkgCd from individual codes
        const mtmkgCd = `${params.comptnCd || ''}-${params.dvsnCd || ''}-${params.wetclsCd || ''}`;
        const saveParams = {
            ...params,
            mtmkgCd: mtmkgCd,
            no: params.no || 1  // Default sequence number
        };

        // Fetch data from the server
        ApiSave('/sys/comptformmng/save/info', saveParams).then(res => {
            if (res && res.success) {
                // Close the modal after saving
                handleToggle();
                // Search for updated data
                props.onGridSearch();
            }
        });
    }
    const onValChange = (e) => {
        const { id, value } = e.target;

        if (id === 'strtDate' || id === 'deadDate') {
            dateChange(id, value);
            return;
        }
        // Validate the specific input field
        validateForm(validateOpt, id);
    }
    const onInputChange = (e) => {
        const { id, value } = e.target;

        // Update the state with the new value only (no validation)
        setParams({ ...params, [id]: value });
    }
    const dateChange = (id, value) => {
        value = value.split('-').join('');

        // Check if the value is an 8-digit number
        if (/^\d{8}$/.test(value)) {
            // Format the date to YYYY-MM-DD
            const year = value.substring(0, 4);
            const month = value.substring(4, 6);
            const day = value.substring(6, 8);
            const formattedValue = `${year}-${month}-${day}`;
            // Check if the formatted value is a valid date
            if (isNaN(new Date(formattedValue))) {
                setParams({ ...params, [id]: '' });
            } else {
                setParams({ ...params, [id]: formattedValue });
            }
        } else {
            // If not an 8-digit number, clear the value
            setParams({ ...params, [id]: '' });
        }

        // deadDate의 값이 strtDate보다 작다면 공백처리
        const startDate = params.strtDate;
        const deadDate = params.deadDate;
        if (startDate && deadDate) {
            const start = new Date(startDate);
            const dead = new Date(deadDate);
            if (dead < start) {
                alert('신청 종료일은 신청 시작일보다 빠를 수 없습니다.');
                setParams({ ...params, [id]: '' });
            }
        }
    }

    // Handle competition code change - fetch related division and weight class data
    const onComptnCdChange = (e) => {
        const { id, value } = e.target;
        setParams({ ...params, [id]: value, dvsnCd: '', wetclsCd: '' });
    }
    
    /****************************
     * Define form config
    *****************************/
    // Dynamic form configuration based on mode
    const isModifyMode = params.mode === 'M';
    const formObj = {
        // Top section - Code selectors (dynamic disabled based on mode)
        comptnCd : formAtt({id: 'comptnCd', label: '대회', value: params['comptnCd'] || '', disabled: isModifyMode, required: true}),
        strtDate : formAtt({id: 'strtDate', label: '신청시작일', value: params['strtDate'] || '', required: true, maxLength: 10, classCol: 'col-4'}),
        deadDate : formAtt({id: 'deadDate', label: '신청종료일', value: params['deadDate'] || '', required: true, maxLength: 10, classCol: 'col-4'}),
        useYn    : formAtt({id: 'useYn', label: '사용여부', value: params['useYn'] || '', required: true, classCol: 'col-4'}),
        
        // Competition Form specific fields based on TB_MNG_COMPTN_FORM
        winPrizeExpn : formAtt({id: 'winPrizeExpn', label: '상금', value: params['winPrizeExpn'] || '', maxLength: 2000}),
        dateExpn : formAtt({id: 'dateExpn', label: '일시', value: params['dateExpn'] || '', maxLength: 2000}),
        locExpn : formAtt({id: 'locExpn', label: '장소', value: params['locExpn'] || '', maxLength: 2000}),
        partFeeExpn : formAtt({id: 'partFeeExpn', label: '참가비', value: params['partFeeExpn'] || '', maxLength: 2000}),
        schlExpn : formAtt({id: 'schlExpn', label: '일정', value: params['schlExpn'] || '', maxLength: 2000}),
        injuryUseYn : formAtt({id: 'injuryUseYn', label: '부상동의사용여부', value: params['injuryUseYn'] || '', required: true, classCol: 'col-6'}),
        videoUseYn : formAtt({id: 'videoUseYn', label: '영상동의사용여부', value: params['videoUseYn'] || '', required: true, classCol: 'col-6'}),
        remark   : formAtt({id: 'remark', label: '비고', value: params['remark'] || '', maxLength: 2000}),
    }

    return (
        <>
            <div 
                className="modal modal-sheet p-1 py-md-5"
                style={{ display: 'none', backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
                tabIndex={-1}
                role="dialog"
                id={rootId}
            >
                <div className="modal-dialog modal-lg">
                    <div className="modal-content rounded-4 shadow">
                        <div className="modal-header border-bottom-0">
                            <h1 className="modal-title fs-5">{title}</h1>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={handleToggle}
                            />
                        </div>
                        <div className="modal-body py-0">
                            <div className="row g-5">
                                <div className="col-lg-12">
                                    <form className="needs-validation" noValidate="">
                                        <div className="row">
                                            {/* Top section: Code selectors */}
                                            <div className="bg-light p-3 rounded">
                                                <div className="row mb-2">
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.comptnCd}
                                                        grpCd={"COMPT"}
                                                        selectType={"S"}
                                                        onChange={onComptnCdChange}
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <CompFmtInput
                                                        attrs={formObj.strtDate}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                    <CompFmtInput
                                                        attrs={formObj.deadDate}
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                    />
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.useYn}
                                                        grpCd={"GRP001"}
                                                        selectType={"S"}
                                                        onChange={(e) => {
                                                            const { id, value } = e.target;
                                                            setParams({ ...params, [id]: value });
                                                            validateForm(validateOpt, id);
                                                        }}
                                                    />
                                                </div>
                                                <div className="row">
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.injuryUseYn}
                                                        grpCd={"GRP001"}
                                                        selectType={"S"}
                                                        onChange={(e) => {
                                                            const { id, value } = e.target;
                                                            setParams({ ...params, [id]: value });
                                                        }}
                                                    />
                                                    <CompFmtSelectbox 
                                                        attrs={formObj.videoUseYn}
                                                        grpCd={"GRP001"}
                                                        selectType={"S"}
                                                        onChange={(e) => {
                                                            const { id, value } = e.target;
                                                            setParams({ ...params, [id]: value });
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            {/* Competition Form fields */}
                                            <div className="p-3 rounded">
                                                <div className="row mb-2">
                                                    <label htmlFor="winPrizeExpn" className="form-label">{formObj.winPrizeExpn.label}</label>
                                                    <textarea
                                                        className="form-control"
                                                        id="winPrizeExpn"
                                                        rows="3"
                                                        value={params.winPrizeExpn || ''}
                                                        maxLength="2000"
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                        placeholder="상금 정보를 입력하세요"
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <label htmlFor="dateExpn" className="form-label">{formObj.dateExpn.label}</label>
                                                    <textarea
                                                        className="form-control"
                                                        id="dateExpn"
                                                        rows="3"
                                                        value={params.dateExpn || ''}
                                                        maxLength="2000"
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                        placeholder="대회 일시 정보를 입력하세요"
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <label htmlFor="locExpn" className="form-label">{formObj.locExpn.label}</label>
                                                    <textarea
                                                        className="form-control"
                                                        id="locExpn"
                                                        rows="3"
                                                        value={params.locExpn || ''}
                                                        maxLength="2000"
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                        placeholder="대회 장소 정보를 입력하세요"
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <label htmlFor="partFeeExpn" className="form-label">{formObj.partFeeExpn.label}</label>
                                                    <textarea
                                                        className="form-control"
                                                        id="partFeeExpn"
                                                        rows="3"
                                                        value={params.partFeeExpn || ''}
                                                        maxLength="2000"
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                        placeholder="참가비 정보를 입력하세요"
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <label htmlFor="schlExpn" className="form-label">{formObj.schlExpn.label}</label>
                                                    <textarea
                                                        className="form-control"
                                                        id="schlExpn"
                                                        rows="3"
                                                        value={params.schlExpn || ''}
                                                        maxLength="2000"
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                        placeholder="대회 일정 정보를 입력하세요"
                                                    />
                                                </div>
                                                <div className="row mb-2">
                                                    <label htmlFor="remark" className="form-label">{formObj.remark.label}</label>
                                                    <textarea
                                                        className="form-control"
                                                        id="remark"
                                                        rows="3"
                                                        value={params.remark || ''}
                                                        maxLength="2000"
                                                        onChange={onInputChange}
                                                        onBlur={onValChange}
                                                        placeholder="기타 비고사항을 입력하세요"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer flex-column align-items-stretch w-100 gap-2 pb-3 border-top-0">
                            <div className="btn-group">
                                <button type="button" className="btn btn-lg btn-primary" onClick={onSave}>
                                    Save changes
                                </button>
                                <button type="button" className="btn btn-lg btn-secondary" data-bs-dismiss="modal" onClick={handleToggle}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </> 
    );
});

export default CompFormCompetitionForm;